import React, { useState, useMemo } from "react"
import moment from "moment"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import Pagination from "~/library/Pagination"
import ButtonGroup from "~/library/ButtonGroup"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { capitalize, formatCurrency } from "~/utils/helpers"
import { apiWorkspacePayments } from "~/routes/"
import type Payment from "~/types/Payment"

const paramifyQ = (q?: Record<string, string | number | null | undefined>) =>
    Object.fromEntries(
        Object.entries(q || {})
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => [`q[${k}]`, String(v)])
    )

const buildRansackQuery = (filters: {
    scope: string
    paymentType: string
    status: string
    search: string
}) => {
    const q: Record<string, string> = {}

    if (filters.scope === "booking") q["payable_type_eq"] = "Booking"
    else if (filters.scope === "sponsorship") q["payable_type_eq"] = "Sponsorship"

    if (filters.paymentType) q["payment_method_eq"] = filters.paymentType
    if (filters.status) q["status_eq"] = filters.status

    if (filters.search) {
        if (!isNaN(Number(filters.search))) {
            q["amount_cents_eq"] = String(Number(filters.search) * 100)
        } else {
            q["client_name_cont"] = filters.search
        }
    }

    return q
}

const PaymentList = () => {
    const { venueSlug } = getAppConfig()

    const [filters, setFilters] = useState({
        scope: "all",
        paymentType: "",
        status: "",
        search: "",
    })

    const {
        data: payments,
        refetch,
        metadata,
        isLoading,
    } = useFetchResource<{ payments: Payment[] }, "payments">(
        apiWorkspacePayments.index.path({ venueSlug }),
        "payments",
        { page: "1", ...paramifyQ(buildRansackQuery(filters)) }
    )

    const handleFilterChange = (key: keyof typeof filters, value: string) => {
        const updated = { ...filters, [key]: value }
        setFilters(updated)
        refetch({ page: "1", ...paramifyQ(buildRansackQuery(updated)) })
    }

    const handlePageChange = (page: string) => {
        refetch({ page, ...paramifyQ(buildRansackQuery(filters)) })
    }

    const columns = useMemo(() => {
        const base = [
            {
                key: "id",
                header: "Payment ID",
                formatter: (id, data) => (
                    <NavigationLink type="payments" to={`/payments/${data.id}`}>
                        INV-{(data.id || "").slice(0, 6).toUpperCase()}
                    </NavigationLink>
                ),
            },
            {
                key: "issueDate",
                header: "Issue Date",
                formatter: (issueDate) =>
                    issueDate ? moment(issueDate).format("MMM Do YYYY") : "—",
            },
            {
                key: "customer",
                header: "Customer",
                formatter: (_, row) => (
                    <div>{row.customer?.name ?? "—"}</div>
                ),
            },
            {
                key: "payment_method",
                header: "Payment Type",
                formatter: (value: string) => (value ? capitalize(value) : "—"),
            },
            {
                key: "status",
                header: "Status",
                formatter: (value: string) => {
                    const label = value
                        ? value.charAt(0).toUpperCase() + value.slice(1)
                        : "—"
                    const badgeClass =
                        {
                            draft: "badge bg-secondary",
                            pending: "badge bg-warning",
                            overdue: "badge bg-danger",
                            paid: "badge bg-success",
                        }[value] || "badge bg-light text-dark"

                    return <span className={badgeClass}>{label}</span>
                },
            },
            {
                key: "amount",
                header: "Amount",
                formatter: (amount) =>
                    formatCurrency({
                        cents: amount.cents,
                        currency_iso: amount.currency_iso,
                    }),
            },
        ]

        if (filters.scope === "all") {
            base.splice(1, 0, {
                key: "payable_type",
                header: "Payment Source",
                formatter: (_, row) => row.type ?? "-",
            })
        }

        return base
    }, [filters.scope])

    return (
        <LoadingWrapper isLoading={isLoading}>
            <PageTitle title="Payments" />

            <div className="row">
                <div className="col-12">
                    <div className="d-flex align-items-center mb-3 gap-3 flex-wrap">

                        <ButtonGroup
                            options={[
                                { label: "All", value: "all" },
                                { label: "Booking", value: "booking" },
                                { label: "Sponsorship", value: "sponsorship" },
                            ]}
                            onClick={(value) => handleFilterChange("scope", value)}
                            selectedValue={filters.scope}
                        />
                        <div className="d-flex gap-2 ms-auto flex-wrap align-items-center">
                            <select
                                className="form-select w-auto"
                                value={filters.paymentType}
                                onChange={(e) => handleFilterChange("paymentType", e.target.value)}
                            >
                                <option value="">All Types</option>
                                <option value="card">Card</option>
                                <option value="paypal">Paypal</option>
                                <option value="paypay">Paypay</option>
                                <option value="cashapp">Cashapp</option>
                            </select>

                            <select
                                className="form-select w-auto"
                                value={filters.status}
                                onChange={(e) => handleFilterChange("status", e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="draft">Draft</option>
                                <option value="pending">Pending</option>
                                <option value="overdue">Overdue</option>
                                <option value="paid">Paid</option>
                            </select>

                            <input
                                type="text"
                                className="form-control w-auto"
                                placeholder="Search"
                                value={filters.search}
                                onChange={(e) => handleFilterChange("search", e.target.value)}
                            />
                        </div>
                    </div>

                    <CatchCard className="card-dark-theme">
                        <CatchTable
                            key={`${filters.scope}-${filters.paymentType}-${filters.status}-${filters.search}`}
                            data={payments}
                            emptyMessage="No Payments"
                            columns={columns}
                        />
                        {metadata && (
                            <Pagination
                                metadata={metadata}
                                onPageChange={(page: number) =>
                                    handlePageChange(page.toString())
                                }
                            />
                        )}
                    </CatchCard>
                </div>
            </div>
        </LoadingWrapper>
    )
}

export default PaymentList
